<script>
import moment from "moment";
import TablePagination from "@/components/Table/TablePagination.vue";
import axios from "axios";

export default {
  name: "GeneratesDetail",
  components: {TablePagination},

  data() {
    return {
      type:  '',
      year:  '',
      month: '',
      tableData: [],
      pagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 0,
        from: 0,
      },
      loading: false,
      breadcrumbs: [
        {text: "Home",          path: "/dashboard", isActive: false},
        {text: "Billing",       path: "",           isActive: false},
        {text: "Generate List", path: "/generates", isActive: false},
        {text: "Details",       path: "",           isActive: true},
      ],
    };
  },

  computed: {
    generateId() {
      return this.$route.params.id;
    },
    formattedMonth() {
      return moment(`${this.year}-${String(this.month).padStart(2, '0')}`).format('MMMM YYYY');
    },
    isReading() {
      return this.type === 'reading';
    },
  },

  created() {
    this.$store.dispatch("Config/setBreadCrumb", this.breadcrumbs);
  },

  mounted() {
    const q = this.$route.query;
    this.type  = q.type  || '';
    this.year  = q.year  || '';
    this.month = q.month || '';
    this.pagination.current_page = q.page     ? parseInt(q.page)     : 1;
    this.pagination.per_page     = q.per_page ? parseInt(q.per_page) : 50;
    this.fetchData();
  },

  methods: {
    fetchData() {
      this.loading = true;
      const endpoint = this.isReading
        ? `/admin/generates/${this.generateId}/reading-details`
        : `/admin/generates/${this.generateId}/bill-details`;

      const params = {
        page:     this.pagination.current_page,
        per_page: this.pagination.per_page,
      };

      axios.get(endpoint, {params})
        .then(res => {
          const d = res.data.data;
          this.tableData = d.data;
          this.pagination = {
            current_page: d.current_page,
            per_page:     parseInt(d.per_page),
            total:        d.total,
            last_page:    d.last_page,
            from:         d.from || 0,
          };
          this.$router.replace({query: {...this.$route.query, page: d.current_page, per_page: d.per_page}}).catch(() => {});
        })
        .catch(err => console.error(err))
        .finally(() => { this.loading = false; });
    },

    onPageChange(page) {
      this.pagination.current_page = page;
      this.fetchData();
    },

    onPageSizeChanged(size) {
      this.pagination.per_page     = size;
      this.pagination.current_page = 1;
      this.fetchData();
    },
  },
};
</script>

<template>
  <div id="printArea">
    <CRow>
      <CCol sm="12">
        <CCard>

          <!-- Header -->
          <CRow class="mb-3">
            <CCol sm="6">
              <h4 class="text-uppercase">
                {{ formattedMonth }} — {{ type }} Details
              </h4>
            </CCol>
            <CCol sm="6" class="text-right">
              <router-link class="btn btn-secondary btn-sm" :to="{name: 'GeneratesList'}">
                ← Back to Generate List
              </router-link>
            </CCol>
          </CRow>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-5">
            <i class="el-icon-loading" style="font-size:32px;"></i>
          </div>

          <!-- Reading table -->
          <div v-else-if="isReading" class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Account No</th>
                  <th>MiCode</th>
                  <th>Batch No</th>
                  <th>PrevRead</th>
                  <th>PrevDate</th>
                  <th>PresRead</th>
                  <th>PresDate</th>
                  <th>Days</th>
                  <th>Consum</th>
                  <th>WaterBill</th>
                  <th>Excise</th>
                  <th>Total</th>
                  <th>EntryDate</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody v-if="tableData.length">
                <tr v-for="(r, index) in tableData" :key="r.id">
                  <td>{{ pagination.from + index }}</td>
                  <td class="font-weight-bold">
                    <router-link :to="`/consumers/${r.customer_account_id}`">
                      {{ r.consumer_account_number }}
                    </router-link>
                  </td>
                  <td>{{ r.meter_inspector && r.meter_inspector.mi_code }}</td>
                  <td>{{ r.batch_no && r.batch_no.batch_no }}</td>
                  <td>{{ r.previous_read }}</td>
                  <td>{{ r.previous_date | dateFormat }}</td>
                  <td>{{ r.present_read }}</td>
                  <td>{{ r.present_date | dateFormat }}</td>
                  <td>{{ r.total_days }}</td>
                  <td>{{ r.consumption }}</td>
                  <td>{{ r.water_bill }}</td>
                  <td>{{ r.excise }}</td>
                  <td>{{ r.total }}</td>
                  <td>{{ r.entry_date | dateFormat }}</td>
                  <td>{{ r.status }}</td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="15" class="text-center">
                    <h5>No reading data found.</h5>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Bill table -->
          <div v-else class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Account No</th>
                  <th>Bill No</th>
                  <th>Issue Date</th>
                  <th>Due Date</th>
                  <th>PrevRead</th>
                  <th>PrevDate</th>
                  <th>PresRead</th>
                  <th>PresDate</th>
                  <th>Days</th>
                  <th>Consum</th>
                  <th>WaterBill</th>
                  <th>Excise</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody v-if="tableData.length">
                <tr v-for="(b, index) in tableData" :key="b.id">
                  <td>{{ pagination.from + index }}</td>
                  <td class="font-weight-bold">
                    <router-link :to="`/consumers/${b.customer_account_id}`">
                      {{ b.consumer_account_number }}
                    </router-link>
                  </td>
                  <td>{{ b.bill_number }}</td>
                  <td>{{ b.issue_date | dateFormat }}</td>
                  <td>{{ b.due_date | dateFormat }}</td>
                  <td>{{ b.previous_reading }}</td>
                  <td>{{ b.previous_reading_date | dateFormat }}</td>
                  <td>{{ b.present_reading }}</td>
                  <td>{{ b.present_reading_date | dateFormat }}</td>
                  <td>{{ b.total_days }}</td>
                  <td>{{ b.used_unit }}</td>
                  <td>{{ b.min_amount }}</td>
                  <td>{{ b.vat }}</td>
                  <td>{{ b.total_amount }}</td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="14" class="text-center">
                    <h5>No bill data found.</h5>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="mt-3" v-if="tableData.length">
            <TablePagination
                :pagination="pagination"
                @pagechanged="onPageChange"
                @pagesizechanged="onPageSizeChanged"
            />
          </div>

        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<style scoped>
.card {
  border: none;
  padding: 15px 15px 0;
}

h4 {
  font-size: 15px;
  font-weight: bold !important;
  color: #000;
}

.table {
  font-size: 14px;
}

.table thead tr th {
  background: #f8f9fa;
  color: #000;
  font-weight: bold;
}

.table tbody {
  font-size: 13px;
  color: #000;
}

.table tbody tr td {
  padding: 0.75rem 0 0.75rem 0.75rem;
}
</style>
